'use client';

import { c as _c } from "react/compiler-runtime";
import SvgInvite from '@common/icon/SvgInvite';
import { cn } from '@core/utils';
import { captureException } from '@sentry/nextjs';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { UnauthApi, UnauthQueryOptions } from '@/api';
import { COOKIE_OPTIONS, COOKIES_KEYS, SEARCH_PARAMS } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useIsOutsideWelloChannel } from '@/hooks/useIsOutsideWelloChannel';
import { useAuthStore } from '@/stores/AuthStore';
import { cookies } from '@/utils/cookies';
const INVITE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 7;
const BUBBLE_DELAY_MS = 1000;
const TOAST_VISIBLE_MS = 4000;
const PREVIEW_INFLOW_KEY = 'invite-preview';
const InviteInflowControllerComponent = () => {
  const $ = _c(27);
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();
  const {
    replace
  } = useCustomRouter();
  const isPartnerWebview = useIsOutsideWelloChannel();
  const myInfo = useAuthStore(_temp);
  const handledInflowKeyRef = useRef(null);
  const [bubbleAmount, setBubbleAmount] = useState(null);
  const [isToastVisible, setIsToastVisible] = useState(false);
  const [isBubbleVisible, setIsBubbleVisible] = useState(false);
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams.get(SEARCH_PARAMS.SHARE_BY);
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const memberCode = t0;
  let t1;
  if ($[2] !== searchParams) {
    t1 = searchParams.has(SEARCH_PARAMS.INVITE_PREVIEW);
    $[2] = searchParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const isPreview = t1;
  let t2;
  let t3;
  if ($[4] !== isPartnerWebview || $[5] !== isPreview || $[6] !== memberCode || $[7] !== myInfo || $[8] !== queryClient || $[9] !== replace) {
    t2 = () => {
      const inflowKey = memberCode ?? (isPreview ? PREVIEW_INFLOW_KEY : null);
      if (!inflowKey || isPartnerWebview || handledInflowKeyRef.current === inflowKey) {
        return;
      }
      if (memberCode && myInfo === undefined) {
        return;
      }
      handledInflowKeyRef.current = inflowKey;
      const url = new URL(window.location.href);
      url.searchParams.delete(SEARCH_PARAMS.SHARE_BY);
      url.searchParams.delete(SEARCH_PARAMS.INVITE_PREVIEW);
      replace(url.toString());
      if (memberCode && !isPreview && myInfo?.memberCode === memberCode) {
        return;
      }
      const findBubbleAmount = memberCode ? UnauthApi["get:/wello/v2/allows/invite/member-codes/{memberCode}"]({
        path: {
          memberCode
        }
      }).then(response => {
        const context = response.data?.context;
        if (!context?.validYN) {
          return;
        }
        if (!isPreview) {
          cookies.set(COOKIES_KEYS.INVITE_MEMBER_CODE, memberCode, {
            ...COOKIE_OPTIONS,
            maxAge: INVITE_COOKIE_MAX_AGE_SECONDS,
            sameSite: "lax" as const
          });
        }
        return context.inviteeUnitAmount ?? 0;
      }) : queryClient.fetchQuery(UnauthQueryOptions["get:/wello/v2/allows/partners/reward-prices"]()).then(_temp2);
      findBubbleAmount.then(amount => {
        if (amount !== undefined) {
          setBubbleAmount(amount);
        }
      }).catch(captureException);
    };
    t3 = [memberCode, isPreview, isPartnerWebview, myInfo, replace, queryClient];
    $[4] = isPartnerWebview;
    $[5] = isPreview;
    $[6] = memberCode;
    $[7] = myInfo;
    $[8] = queryClient;
    $[9] = replace;
    $[10] = t2;
    $[11] = t3;
  } else {
    t2 = $[10];
    t3 = $[11];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[12] !== bubbleAmount) {
    t4 = () => {
      if (bubbleAmount === null) {
        return;
      }
      const showToastTimer = setTimeout(() => setIsToastVisible(true), 0);
      const showBubbleTimer = setTimeout(() => setIsBubbleVisible(true), BUBBLE_DELAY_MS);
      const hideTimer = setTimeout(() => {
        setIsToastVisible(false);
        setIsBubbleVisible(false);
      }, TOAST_VISIBLE_MS);
      return () => {
        clearTimeout(showToastTimer);
        clearTimeout(showBubbleTimer);
        clearTimeout(hideTimer);
      };
    };
    t5 = [bubbleAmount];
    $[12] = bubbleAmount;
    $[13] = t4;
    $[14] = t5;
  } else {
    t4 = $[13];
    t5 = $[14];
  }
  useEffect(t4, t5);
  if (bubbleAmount === null) {
    return null;
  }
  let t6;
  if ($[15] !== bubbleAmount || $[16] !== isBubbleVisible) {
    t6 = bubbleAmount > 0 && <div role="status" aria-hidden={!isBubbleVisible} className={cn("relative origin-bottom whitespace-nowrap rounded-xsmall bg-Gray-black/80 px-12 py-8 text-Gray-white backdrop-blur-sm transition-[opacity,transform] duration-200 ease-out typo-caption1 motion-reduce:transition-opacity", isBubbleVisible ? "scale-100 opacity-100" : "scale-[0.1] opacity-0 motion-reduce:scale-100")}><span className={cn("block transition-[opacity,transform] duration-200 ease-out", isBubbleVisible ? "translate-y-0 opacity-100 delay-200 motion-reduce:delay-0" : "-translate-y-4 opacity-0 motion-reduce:translate-y-0")}>10만원 기부시 웰포인트 {bubbleAmount.toLocaleString("ko-KR")}원 지급</span><span className="absolute left-1/2 top-full -translate-x-1/2 border-x-4 border-t-8 border-x-transparent border-t-Gray-black/80" /></div>;
    $[15] = bubbleAmount;
    $[16] = isBubbleVisible;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const t7 = !isToastVisible;
  const t8 = isToastVisible ? "opacity-100" : "opacity-0";
  let t9;
  if ($[18] !== t8) {
    t9 = cn("flex h-48 items-center gap-8 whitespace-nowrap rounded-pill bg-Gray-black px-20 text-Gray-white transition-opacity duration-300 typo-body4 motion-reduce:transition-none", t8);
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <SvgInvite className="size-20" originalFill={true} />;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t7 || $[22] !== t9) {
    t11 = <div role="status" aria-hidden={t7} className={t9}>{t10}친구의 초대로 방문하셨네요!</div>;
    $[21] = t7;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t6) {
    t12 = <div className="pointer-events-none fixed bottom-[var(--toast-bottom)] left-1/2 z-[9998] flex -translate-x-1/2 flex-col items-center gap-8">{t6}{t11}</div>;
    $[24] = t11;
    $[25] = t6;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
};
export const InviteInflowController = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ErrorBoundary fallback={null}><Suspense fallback={null}><InviteInflowControllerComponent /></Suspense></ErrorBoundary>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
function _temp(state) {
  return state.myInfo;
}
function _temp2(t0) {
  const {
    context: context_0
  } = t0;
  return context_0?.inviteeUnitAmount ?? 0;
}